import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { accessStatusAtom, trcUserAtom } from '../atoms/trc-global';
import api from '../../data/api';
import Toast from '../../utils/toastManager';
import { userInfoAtom } from '../../data/atoms/utilsAtoms';
import { useEffect, useRef, useState } from 'react';
import ACCESS_STATUS from '../constants/accessStatus';
import { useNavigate } from 'react-router-dom';
import { getSignupRoute } from '../../constants/featureFlags';

export const useAccess = () => {
	const navigate = useNavigate();
	const userData = useAtomValue(userInfoAtom);
	const [accessStatus, setAccessStatus] = useAtom(accessStatusAtom);
	const setTrcUser = useSetAtom(trcUserAtom);
	const [loading, setLoading] = useState(false);
	const [initialLoad, setInitialLoad] = useState(!accessStatus);
	const hasFetched = useRef(false);

	useEffect(() => {
		if (!userData?.user?.email) {
			setInitialLoad(false);
			Toast.error('Please sign up in the website');
			navigate(getSignupRoute());
			return;
		}

		if (hasFetched.current) return;
		hasFetched.current = true;

		const fetchStatus = async () => {
			try {
				const response = await api.getStatus(userData.user.email);
				setTrcUser(response?.data);
				setAccessStatus(response?.data?.accessStatus);
			} catch (err) {
				Toast.error(err.message || 'Failed to fetch status. Please try again.');
			} finally {
				setInitialLoad(false);
			}
		};

		fetchStatus();
	}, [userData?.user?.email]);

	const requestAccess = async formDataObj => {
		setLoading(true);
		try {
			const response = await api.requestAccess(formDataObj);
			if (response?.data) {
				setTrcUser(response.data);
				setAccessStatus(ACCESS_STATUS.PENDING);
			}
		} catch (err) {
			Toast.error(err.message || 'Error requesting access. Please try again.');
		} finally {
			setLoading(false);
		}
	};

	const approveAccess = async userId => {
		setLoading(true);
		try {
			const response = await api.responseAccess({ userId, status: ACCESS_STATUS.APPROVED });
			if (response?.data) {
				setTrcUser(response.data);
				setAccessStatus(ACCESS_STATUS.APPROVED);
			}
		} catch (err) {
			Toast.error(err.message || 'Error approving access. Please try again.');
		} finally {
			setLoading(false);
		}
	};

	const rejectAccess = async userId => {
		setLoading(true);
		try {
			const response = await api.responseAccess({ userId, status: ACCESS_STATUS.NO_ACCESS });
			if (response?.data) {
				setTrcUser(response.data);
				setAccessStatus(ACCESS_STATUS.NO_ACCESS);
			}
		} catch (err) {
			Toast.error(err.message || 'Error rejecting access. Please try again.');
		} finally {
			setLoading(false);
		}
	};

	return {
		loading,
		initialLoad,
		accessStatus,
		requestAccess,
		approveAccess,
		rejectAccess,
		hasAccess: accessStatus === ACCESS_STATUS.APPROVED,
		isPending: accessStatus === ACCESS_STATUS.PENDING,
		needsAccess: accessStatus === ACCESS_STATUS.NO_ACCESS
	};
};

export default useAccess;
